import { useDocumentTitle } from "@/lib/use-document-title";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AccessBranding } from "@/components/AccessBranding";
import { PHONE_BRANDS, OPERATORS, INTERESTS } from "@/lib/constants";
import { useStores } from "@/lib/data-hooks";
import { Smartphone, Heart, Sparkles, KeyRound, CheckCircle2, ArrowRight, ArrowLeft, Store, User } from "lucide-react";
import { toast } from "sonner";

type FormState = {
  ssn: string;
  address: string;
  zip: string;
  city: string;
  storeId: string;
  phoneBrand: string;
  phoneModel: string;
  operator: string;
  operatorCustom: string;
  interests: string[];
  interestsText: string;
  source: string;
  customerType: string;
  notes: string;
};

const EMPTY: FormState = {
  ssn: "", address: "", zip: "", city: "",
  storeId: "", phoneBrand: "", phoneModel: "", operator: "", operatorCustom: "",
  interests: [], interestsText: "",
  source: "", customerType: "", notes: "",
};

function normalizeSsn(s: string) {
  return s.replace(/\s/g, "").trim();
}
function isValidSsn(s: string) {
  const v = normalizeSsn(s);
  // Tillåt 10 eller 12 siffror, med eller utan bindestreck/+
  return /^(\d{6}|\d{8})[-+]?\d{4}$/.test(v);
}

export default function AccessAktivera() {
  useDocumentTitle("Aktivera ditt konto — Lagamobilen Access");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromCheckout = searchParams.get("from") === "checkout";
  const { data: stores = [] } = useStores();

  const [ready, setReady] = useState(false);
  // Lösenordssteget behövs bara direkt efter checkout (då kunden loggats in via magisk länk
  // och ännu inte satt något lösenord). Befintliga inloggade kunder som slutför sin profil
  // hoppar direkt till uppgifterna.
  const [step, setStep] = useState(fromCheckout ? 1 : 2);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN" || event === "INITIAL_SESSION") {
        supabase.auth.getSession().then(({ data }) => { if (data.session) setReady(true); });
      }
    });
    supabase.auth.getSession().then(({ data }) => { if (data.session) setReady(true); });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!ready) return;
    (async () => {
      const { error: rpcErr } = await supabase.rpc("ensure_member_for_current_user" as any);
      if (rpcErr) console.error("ensure_member_for_current_user failed:", rpcErr);
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data: m } = await supabase
        .from("members")
        .select("ssn, address, zip, city, store_id, phone_brand, phone_model, operator, interests, interests_text, source, customer_type, notes, notify_email, notify_sms")
        .eq("user_id", u.user.id)
        .maybeSingle();
      if (m) {
        const savedOp = (m as any).operator || "";
        const isKnown = OPERATORS.includes(savedOp);
        setForm({
          ssn: (m as any).ssn || "",
          address: (m as any).address || "",
          zip: (m as any).zip || "",
          city: (m as any).city || "",
          storeId: (m as any).store_id || "",
          phoneBrand: (m as any).phone_brand || "",
          phoneModel: (m as any).phone_model || "",
          operator: isKnown ? savedOp : savedOp ? "Annan" : "",
          operatorCustom: isKnown ? "" : savedOp,
          interests: ((m as any).interests as string[]) || [],
          interestsText: (m as any).interests_text || "",
          source: (m as any).source || "",
          customerType: (m as any).customer_type || "",
          notes: (m as any).notes || "",
        });
        if (typeof (m as any).notify_email === "boolean") setNotifyEmail((m as any).notify_email);
        if (typeof (m as any).notify_sms === "boolean") setNotifySms((m as any).notify_sms);
      }
    })();
  }, [ready]);

  const toggleInterest = (v: string) =>
    setForm(f => ({ ...f, interests: f.interests.includes(v) ? f.interests.filter(x => x !== v) : [...f.interests, v] }));

  async function handlePasswordStep(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    if (pwd.length < 8) return setErr("Lösenordet måste vara minst 8 tecken");
    if (pwd !== pwd2) return setErr("Lösenorden matchar inte");
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pwd });
    setBusy(false);
    if (error) return setErr(error.message);
    setStep(2);
  }

  function validateStep(n: number): string | null {
    if (n === 2) {
      if (!form.ssn.trim()) return "Ange ditt personnummer.";
      if (!isValidSsn(form.ssn)) return "Personnumret ser inte korrekt ut (ÅÅÅÅMMDD-XXXX).";
      if (!form.address.trim()) return "Ange din gatuadress.";
      if (!form.zip.trim()) return "Ange ditt postnummer.";
      if (!form.city.trim()) return "Ange din ort.";
    }
    if (n === 3) {
      if (!form.storeId) return "Välj din närmaste/favoritbutik.";
      if (!form.phoneBrand) return "Välj telefonmärke.";
      if (!form.operator) return "Välj operatör.";
      if (form.operator === "Annan" && !form.operatorCustom.trim()) return "Skriv in din operatör.";
    }
    if (n === 4) {
      if (!form.interests || form.interests.length === 0) return "Välj minst ett intresse (eller skriv något i fritextfältet).";
    }
    if (n === 5) {
      if (!form.source) return "Välj hur du hörde om Access.";
      if (!form.customerType) return "Välj om du är privatperson eller företag.";
    }
    return null;
  }

  async function persistPartial() {
    try {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const operatorValue = form.operator === "Annan" ? form.operatorCustom.trim() : form.operator || null;
      const payload: Record<string, any> = {
        ssn: form.ssn ? normalizeSsn(form.ssn) : null,
        address: form.address.trim() || null,
        zip: form.zip.trim() || null,
        city: form.city.trim() || null,
        store_id: form.storeId || null,
        phone_brand: form.phoneBrand || null,
        phone_model: form.phoneModel || null,
        operator: operatorValue,
        interests: form.interests,
        interests_text: form.interestsText || null,
        source: form.source || null,
        customer_type: form.customerType || null,
        notes: form.notes || null,
        notify_email: notifyEmail,
        notify_sms: notifySms,
      };
      // Ta bort null-värden för att inte skriva över existerande data med null i tidiga steg
      Object.keys(payload).forEach(k => {
        if (payload[k] === null || (Array.isArray(payload[k]) && payload[k].length === 0)) delete payload[k];
      });
      if (Object.keys(payload).length === 0) return;
      const { error } = await supabase.from("members").update(payload as any).eq("user_id", u.user.id);
      if (error) console.warn("[aktivera] partial save failed", error);
    } catch (e) {
      console.warn("[aktivera] partial save error", e);
    }
  }

  async function next() {
    const v = validateStep(step);
    if (v) { toast.error(v); return; }
    await persistPartial();
    setStep(s => s + 1);
  }

  async function finish() {
    for (const n of [2, 3, 4, 5]) {
      const v = validateStep(n);
      if (v) { setStep(n); toast.error(v); return; }
    }


    setBusy(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("Ingen aktiv session.");

      const operatorValue = form.operator === "Annan" ? form.operatorCustom.trim() : form.operator || null;
      const payload: Record<string, any> = {
        ssn: normalizeSsn(form.ssn),
        address: form.address.trim() || null,
        zip: form.zip.trim() || null,
        city: form.city.trim() || null,
        store_id: form.storeId || null,
        phone_brand: form.phoneBrand || null,
        phone_model: form.phoneModel || null,
        operator: operatorValue,
        interests: form.interests,
        interests_text: form.interestsText || null,
        source: form.source || null,
        customer_type: form.customerType || null,
        notes: form.notes || null,
        notify_email: notifyEmail,
        notify_sms: notifySms,
      };

      const { data, error } = await supabase
        .from("members")
        .update(payload as any)
        .eq("user_id", u.user.id)
        .select("id");

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("Ingen medlemsrad hittades för ditt konto.");
      }

      setOk(true);
      setTimeout(() => navigate("/access/dashboard"), 1200);
    } catch (e: any) {
      console.error("[aktivera] finish failed", e);
      toast.error(e?.message || "Kunde inte spara uppgifterna.");
    } finally {
      setBusy(false);
    }
  }

  const steps = [
    { n: 1, label: "Lösenord", icon: KeyRound },
    { n: 2, label: "Om dig", icon: User },
    { n: 3, label: "Din mobil", icon: Smartphone },
    { n: 4, label: "Intressen", icon: Heart },
    { n: 5, label: "Övrigt", icon: Sparkles },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-ink)] flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8"><AccessBranding /></div>
        <div className="card-soft p-6 sm:p-8">
          {ok ? (
            <p className="text-center text-foreground">Konto aktiverat! Tar dig till din dashboard…</p>
          ) : !ready ? (
            <p className="text-center text-muted-foreground">Verifierar din aktiveringslänk…</p>
          ) : (
            <>
              <h1 className="!text-2xl text-center mb-1">Aktivera ditt konto</h1>
              <p className="text-center text-muted-foreground text-sm mb-6">
                Välj lösenord och berätta lite om dig — så är du klar.
              </p>

              {/* Stepper */}
              <div className="flex items-center justify-between mb-8">
                {steps.map((s, i) => {
                  const Icon = s.icon;
                  const active = step === s.n;
                  const done = step > s.n;
                  return (
                    <div key={s.n} className="flex items-center flex-1 last:flex-none">
                      <div className={`flex items-center gap-2 ${active ? "text-foreground" : done ? "text-[var(--color-success)]" : "text-muted-foreground/60"}`}>
                        <div className={`size-9 rounded-full flex items-center justify-center transition ${
                          active ? "bg-[var(--color-primary)] text-white" :
                          done ? "bg-[var(--color-success)]/15 text-[var(--color-success)]" :
                          "bg-muted text-muted-foreground"
                        }`}>
                          {done ? <CheckCircle2 size={16} /> : <Icon size={16} />}
                        </div>
                        <span className="hidden sm:inline text-xs font-display font-semibold">{s.label}</span>
                      </div>
                      {i < steps.length - 1 && <div className={`flex-1 h-px mx-3 ${done ? "bg-[var(--color-success)]/40" : "bg-border"}`} />}
                    </div>
                  );
                })}
              </div>

              {step === 1 && (
                <form onSubmit={handlePasswordStep}>
                  <h2 className="!text-xl mb-1">Välj ett lösenord</h2>
                  <p className="text-sm text-muted-foreground mb-6">Minst 8 tecken.</p>

                  <label className="label-field">Lösenord</label>
                  <input type="password" className="input-field" value={pwd} onChange={e => setPwd(e.target.value)} autoFocus />

                  <label className="label-field mt-3">Bekräfta lösenord</label>
                  <input type="password" className="input-field" value={pwd2} onChange={e => setPwd2(e.target.value)} />

                  <div className="mt-6 pt-5 border-t border-border">
                    <p className="label-field !mb-3">Hur vill du få nyheter om ditt medlemskap?</p>
                    <label className="flex items-center gap-2.5 text-sm cursor-pointer mb-2">
                      <input type="checkbox" className="accent-[var(--color-primary)]" checked={notifyEmail} onChange={e => setNotifyEmail(e.target.checked)} />
                      Via e-post
                    </label>
                    <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input type="checkbox" className="accent-[var(--color-primary)]" checked={notifySms} onChange={e => setNotifySms(e.target.checked)} />
                      Via SMS
                    </label>
                  </div>

                  {err && <p className="text-destructive text-xs mt-3">{err}</p>}
                  <div className="flex justify-end mt-6">
                    <button type="submit" disabled={busy} className="btn-pill disabled:opacity-50">
                      {busy ? "Sparar…" : <>Nästa <ArrowRight size={14} /></>}
                    </button>
                  </div>
                </form>
              )}

              {step === 2 && (
                <>
                  <h2 className="!text-xl mb-1">Om dig</h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    Vi behöver dessa uppgifter för fakturering, leveranser och för att kunna identifiera dig i butik.
                  </p>

                  <label className="label-field">Personnummer</label>
                  <input
                    className="input-field"
                    placeholder="ÅÅÅÅMMDD-XXXX"
                    inputMode="numeric"
                    value={form.ssn}
                    onChange={e => setForm({ ...form, ssn: e.target.value })}
                  />

                  <label className="label-field mt-4">Gatuadress</label>
                  <input
                    className="input-field"
                    placeholder="t.ex. Storgatan 12"
                    value={form.address}
                    onChange={e => setForm({ ...form, address: e.target.value })}
                  />

                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <div>
                      <label className="label-field">Postnummer</label>
                      <input
                        className="input-field"
                        placeholder="123 45"
                        inputMode="numeric"
                        value={form.zip}
                        onChange={e => setForm({ ...form, zip: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="label-field">Ort</label>
                      <input
                        className="input-field"
                        placeholder="t.ex. Stockholm"
                        value={form.city}
                        onChange={e => setForm({ ...form, city: e.target.value })}
                      />
                    </div>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <h2 className="!text-xl mb-1">Din mobila vardag</h2>
                  <p className="text-sm text-muted-foreground mb-6">Hjälper oss att ge dig rätt tips, tillbehör och support från rätt butik.</p>

                  <label className="label-field"><Store size={12} className="inline mr-1" /> Närmaste/favoritbutik</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                    {stores.map((s: any) => (
                      <button key={s.id} type="button" onClick={() => setForm({ ...form, storeId: s.id })}
                        className={`px-3 py-2.5 rounded-lg border text-sm font-display font-semibold transition ${form.storeId === s.id ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}>
                        {s.name}
                      </button>
                    ))}
                  </div>

                  <label className="label-field mt-4">Vilken typ av telefon?</label>
                  <select className="input-field" value={form.phoneBrand} onChange={e => setForm({ ...form, phoneBrand: e.target.value })}>
                    <option value="">– Välj märke –</option>
                    {PHONE_BRANDS.map(b => <option key={b}>{b}</option>)}
                  </select>

                  <label className="label-field mt-4">Modell (valfritt)</label>
                  <input className="input-field" placeholder="t.ex. iPhone 15 Pro" value={form.phoneModel} onChange={e => setForm({ ...form, phoneModel: e.target.value })} />

                  <label className="label-field mt-4">Operatör</label>
                  <select className="input-field" value={form.operator} onChange={e => setForm({ ...form, operator: e.target.value, operatorCustom: e.target.value === "Annan" ? form.operatorCustom : "" })}>
                    <option value="">– Välj –</option>
                    {OPERATORS.map(o => <option key={o}>{o}</option>)}
                  </select>
                  {form.operator === "Annan" && (
                    <>
                      <label className="label-field mt-3">Annan operatör</label>
                      <input className="input-field" placeholder="Skriv in din operatör …"
                        value={form.operatorCustom} onChange={e => setForm({ ...form, operatorCustom: e.target.value })} />
                    </>
                  )}
                </>
              )}

              {step === 4 && (
                <>
                  <h2 className="!text-xl mb-1">Intressen & preferenser</h2>
                  <p className="text-sm text-muted-foreground mb-6">Vad vill du lära dig mer om? Välj så många du vill.</p>

                  <div className="grid sm:grid-cols-2 gap-x-5 gap-y-2 text-sm">
                    {INTERESTS.map(i => (
                      <label key={i} className="flex items-center gap-2 cursor-pointer py-1">
                        <input type="checkbox" className="accent-[var(--color-primary)]" checked={form.interests.includes(i)} onChange={() => toggleInterest(i)} />
                        {i}
                      </label>
                    ))}
                  </div>

                  <label className="label-field mt-5">Något annat du är nyfiken på?</label>
                  <textarea className="input-field" rows={3} placeholder="Skriv fritt …" value={form.interestsText} onChange={e => setForm({ ...form, interestsText: e.target.value })} />
                </>
              )}

              {step === 5 && (
                <>
                  <h2 className="!text-xl mb-1">Övrigt</h2>
                  <p className="text-sm text-muted-foreground mb-6">Sista steget – sen är du klar!</p>

                  <label className="label-field">Hur hörde du talas om Access?</label>
                  <select className="input-field" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                    <option value="">– Välj –</option>
                    <option>I butik</option>
                    <option>Vän/familj</option>
                    <option>Sociala medier</option>
                    <option>Webben</option>
                    <option>Annat</option>
                  </select>

                  <label className="label-field mt-4">Företag eller privatperson?</label>
                  <select className="input-field" value={form.customerType} onChange={e => setForm({ ...form, customerType: e.target.value })}>
                    <option value="">– Välj –</option>
                    <option>Privat</option>
                    <option>Företag</option>
                  </select>

                  <label className="label-field mt-4">Särskilda önskemål eller behov? (valfritt)</label>
                  <textarea className="input-field" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                </>
              )}

              {step > 1 && (
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                  {step > 2 ? (
                    <button onClick={() => setStep(s => s - 1)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition">
                      <ArrowLeft size={14} /> Tillbaka
                    </button>
                  ) : <span />}

                  {step < 5 ? (
                    <button onClick={next} disabled={busy} className="btn-pill disabled:opacity-50">
                      Nästa <ArrowRight size={14} />
                    </button>
                  ) : (
                    <button onClick={finish} disabled={busy} className="btn-pill disabled:opacity-50">
                      {busy ? "Sparar…" : "Slutför aktivering"}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
